// Layout-context: één vensterbreedte en één drawer-toestand voor de hele app.
//
// TWEE CONTEXTEN, BEWUST
// ----------------------
// `vw` verandert bij ELKE pixel tijdens het slepen van het venster. Zaten `isNarrow` en
// `navOpen` in dezelfde context-value, dan krijgt die value per pixel een nieuwe identity
// en rendert de hele shell (zijbalk, topbar, save-bar) mee — voor een boolean die alleen
// op 1400px omklapt. Vandaar:
//
//   ViewportContext : { vw }                                    → Assets en SAA
//   NavContext      : { isNarrow, navOpen, toggleNav, closeNav } → shell en vaste balken
//
// Een scherm dat alleen "smal of niet" wil weten gebruikt useIsNarrow() en rendert dus
// niet mee met elke pixel. Voeg deze twee niet samen.

import React, { createContext, useContext, useMemo } from "react";

const ViewportContext = createContext(null);
const NavContext = createContext(null);

export function LayoutProvider({ vw, isNarrow, navOpen, toggleNav, closeNav, children }) {
  const viewportValue = useMemo(() => ({ vw }), [vw]);
  const navValue = useMemo(
    () => ({ isNarrow, navOpen, toggleNav, closeNav }),
    [isNarrow, navOpen, toggleNav, closeNav]
  );

  return (
    <ViewportContext.Provider value={viewportValue}>
      <NavContext.Provider value={navValue}>{children}</NavContext.Provider>
    </ViewportContext.Provider>
  );
}

// De vensterbreedte in px. Buiten een provider — een component-test die de shell niet
// opzet — geven we een breed venster terug: dan krijgt zo'n test de volledige weergave
// en niet stilletjes de compacte.
export function useViewportWidthValue() {
  return useContext(ViewportContext)?.vw ?? 1440;
}

// Staat de app in drawer-modus? Verandert alleen op de grens, niet per pixel.
export function useIsNarrow() {
  return useContext(NavContext)?.isNarrow ?? false;
}

export function useNav() {
  return (
    useContext(NavContext) ?? {
      isNarrow: false,
      navOpen: false,
      toggleNav: null,
      closeNav: () => {},
    }
  );
}

export default LayoutProvider;
